/**
 * Bundles.js
 * ==========
 * Licence bundle management page.
 * Lists all bundles, supports inline add and edit, and soft-deactivation.
 *
 * Wire up:
 *   1. Add to src/pages/Bundles.js
 *   2. In App.js add { id: 'bundles', label: 'Bundles' } to NAV
 *   3. Add  bundles: <Bundles />  to the pages map
 *   4. Add to api.js:
 *        export const getBundles      = ()       => api.get('/bundles');
 *        export const createBundle    = (body)   => api.post('/bundles', body);
 *        export const updateBundle    = (id, body) => api.patch(`/bundles/${id}`, body);
 */

import React, { useEffect, useState, useCallback } from 'react';
import { getBundles, createBundle, updateBundle } from '../api';

// ── Helpers ───────────────────────────────────────────────────────────────────

const fmtNum = n => n == null ? 'Unlimited' : Number(n).toLocaleString();
const fmtGb  = g => g == null ? 'Unlimited' : `${Number(g).toLocaleString()} GB`;

const EMPTY_FORM = {
  name: '', description: '', sample_limit: '', test_limit: '', storage_limit_gb: '',
};

function parseLimit(val) {
  if (val === '' || val === null || val === undefined) return null;
  const n = Number(val);
  return isNaN(n) ? null : n;
}

// ── Inline form (shared by add and edit) ──────────────────────────────────────

function BundleForm({ initial = EMPTY_FORM, onSave, onCancel, saving, error }) {
  const [form, setForm] = useState(initial);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const F = { fontSize: 13, padding: '7px 10px', border: '1px solid #e5e5e5', borderRadius: 6, background: '#fff', outline: 'none', width: '100%' };
  const L = { fontSize: 12, color: '#666', marginBottom: 4, display: 'block' };

  return (
    <div>
      {/* Row 1: name + description */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 12, marginBottom: 12 }}>
        <div>
          <label style={L}>Name <span style={{ color: '#ef4444' }}>*</span></label>
          <input style={F} placeholder="e.g. Growth" value={form.name} onChange={e => set('name', e.target.value)} />
        </div>
        <div>
          <label style={L}>Description</label>
          <input style={F} placeholder="Short description of this bundle" value={form.description} onChange={e => set('description', e.target.value)} />
        </div>
      </div>

      {/* Row 2: limits */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 14 }}>
        <div>
          <label style={L}>Sample limit <span style={{ fontSize: 11, color: '#aaa' }}>(blank = unlimited)</span></label>
          <input style={F} type="number" min="0" placeholder="e.g. 75000" value={form.sample_limit} onChange={e => set('sample_limit', e.target.value)} />
        </div>
        <div>
          <label style={L}>Test limit <span style={{ fontSize: 11, color: '#aaa' }}>(blank = unlimited)</span></label>
          <input style={F} type="number" min="0" placeholder="e.g. 150000" value={form.test_limit} onChange={e => set('test_limit', e.target.value)} />
        </div>
        <div>
          <label style={L}>Storage limit (GB) <span style={{ fontSize: 11, color: '#aaa' }}>(blank = unlimited)</span></label>
          <input style={F} type="number" min="0" placeholder="e.g. 250" value={form.storage_limit_gb} onChange={e => set('storage_limit_gb', e.target.value)} />
        </div>
      </div>

      {error && (
        <div style={{ marginBottom: 12, fontSize: 12, color: '#991b1b', background: '#fef2f2', padding: '7px 12px', borderRadius: 6 }}>
          {error}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8 }}>
        <button
          onClick={() => onSave({
            name:             form.name.trim(),
            description:      form.description.trim() || null,
            sample_limit:     parseLimit(form.sample_limit),
            test_limit:       parseLimit(form.test_limit),
            storage_limit_gb: parseLimit(form.storage_limit_gb),
          })}
          disabled={!form.name.trim() || saving}
          style={{ padding: '7px 18px', fontSize: 13, borderRadius: 6, border: 'none', background: form.name.trim() ? '#1a1a1a' : '#e5e5e5', color: form.name.trim() ? '#fff' : '#aaa', cursor: form.name.trim() ? 'pointer' : 'not-allowed', fontWeight: 500 }}
        >
          {saving ? 'Saving…' : 'Save bundle'}
        </button>
        <button
          onClick={onCancel}
          style={{ padding: '7px 14px', fontSize: 13, borderRadius: 6, border: '1px solid #e5e5e5', background: '#fff', cursor: 'pointer', color: '#555' }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

// ── Bundle card ───────────────────────────────────────────────────────────────

function BundleCard({ bundle, onUpdated }) {
  const [editing, setEditing] = useState(false);
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState('');

  const initialForm = {
    name:             bundle.name,
    description:      bundle.description || '',
    sample_limit:     bundle.sample_limit     ?? '',
    test_limit:       bundle.test_limit       ?? '',
    storage_limit_gb: bundle.storage_limit_gb ?? '',
  };

  async function handleSave(body) {
    setSaving(true); setError('');
    try {
      const r = await updateBundle(bundle.id, body);
      onUpdated(r.data);
      setEditing(false);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleToggleActive() {
    setSaving(true); setError('');
    try {
      const r = await updateBundle(bundle.id, { is_active: !bundle.is_active });
      onUpdated(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  }

  const inactive = !bundle.is_active;

  return (
    <div style={{
      background: '#fff', border: `1px solid ${editing ? '#378ADD' : '#e5e5e5'}`,
      borderRadius: 10, padding: '18px 20px', marginBottom: 12,
      opacity: inactive ? 0.6 : 1,
      transition: 'border-color 0.15s, opacity 0.15s',
    }}>
      {editing ? (
        <>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 14 }}>Edit bundle</div>
          <BundleForm
            initial={initialForm}
            onSave={handleSave}
            onCancel={() => { setEditing(false); setError(''); }}
            saving={saving}
            error={error}
          />
        </>
      ) : (
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>

          {/* Left: name + description + limits */}
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
              <span style={{ fontSize: 15, fontWeight: 600 }}>{bundle.name}</span>
              {inactive && (
                <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 20, background: '#f3f4f6', color: '#6b7280', fontWeight: 500 }}>
                  Inactive
                </span>
              )}
            </div>
            {bundle.description && (
              <div style={{ fontSize: 13, color: '#666', marginBottom: 10 }}>{bundle.description}</div>
            )}

            {/* Limit pills */}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {[
                { label: 'Samples', value: fmtNum(bundle.sample_limit),     color: '#378ADD' },
                { label: 'Tests',   value: fmtNum(bundle.test_limit),       color: '#1D9E75' },
                { label: 'Storage', value: fmtGb(bundle.storage_limit_gb),  color: '#7F77DD' },
              ].map(({ label, value, color }) => (
                <div key={label} style={{
                  background: `${color}10`, border: `1px solid ${color}30`,
                  borderRadius: 8, padding: '6px 12px', fontSize: 12,
                }}>
                  <span style={{ color: '#888', marginRight: 5 }}>{label}</span>
                  <span style={{ fontWeight: 600, color }}>{value}</span>
                </div>
              ))}
            </div>

            {error && (
              <div style={{ marginTop: 8, fontSize: 12, color: '#991b1b' }}>{error}</div>
            )}
          </div>

          {/* Right: actions */}
          <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
            {!inactive && (
              <button
                onClick={() => setEditing(true)}
                style={{ padding: '6px 12px', fontSize: 12, borderRadius: 6, border: '1px solid #e5e5e5', background: '#fff', cursor: 'pointer', color: '#555' }}
              >
                ✎ Edit
              </button>
            )}
            <button
              onClick={handleToggleActive}
              disabled={saving}
              style={{ padding: '6px 12px', fontSize: 12, borderRadius: 6, border: `1px solid ${inactive ? '#bbf7d0' : '#fecaca'}`, background: inactive ? '#f0fdf4' : '#fff', cursor: 'pointer', color: inactive ? '#166534' : '#dc2626' }}
            >
              {saving ? '…' : inactive ? 'Reactivate' : 'Deactivate'}
            </button>
          </div>

        </div>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function Bundles() {
  const [bundles,      setBundles]      = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState(null);
  const [showAdd,      setShowAdd]      = useState(false);
  const [addSaving,    setAddSaving]    = useState(false);
  const [addError,     setAddError]     = useState('');
  const [showInactive, setShowInactive] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    getBundles()
      .then(r => { setBundles(r.data); setError(null); })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  async function handleCreate(body) {
    setAddSaving(true); setAddError('');
    try {
      const r = await createBundle(body);
      setBundles(bs => [...bs, r.data]);
      setShowAdd(false);
    } catch (e) {
      setAddError(e.response?.data?.detail || e.message);
    } finally {
      setAddSaving(false);
    }
  }

  function handleUpdated(updated) {
    setBundles(bs => bs.map(b => b.id === updated.id ? updated : b));
  }

  if (loading) return <div className="loading">Loading bundles…</div>;
  if (error)   return <div className="error">Error: {error}</div>;

  const active   = bundles.filter(b =>  b.is_active);
  const inactive = bundles.filter(b => !b.is_active);

  return (
    <>
      {/* ── Summary metrics ── */}
      <div className="metrics" style={{ gridTemplateColumns: 'repeat(3, minmax(0,1fr))', marginBottom: 20 }}>
        <div className="metric">
          <div className="metric-label">Active bundles</div>
          <div className="metric-value">{active.length}</div>
          <div className="metric-sub">available for assignment</div>
        </div>
        <div className="metric">
          <div className="metric-label">Inactive bundles</div>
          <div className="metric-value">{inactive.length}</div>
          <div className="metric-sub">hidden from assignment</div>
        </div>
        <div className="metric">
          <div className="metric-label">Total bundles</div>
          <div className="metric-value">{bundles.length}</div>
        </div>
      </div>

      {/* ── Add button ── */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <button
          onClick={() => setShowAdd(f => !f)}
          style={{ padding: '8px 16px', fontSize: 13, borderRadius: 6, border: 'none', background: showAdd ? '#f3f4f6' : '#1a1a1a', color: showAdd ? '#555' : '#fff', cursor: 'pointer', fontWeight: 500 }}
        >
          {showAdd ? '✕ Cancel' : '+ Add bundle'}
        </button>
      </div>

      {/* ── Inline add form ── */}
      {showAdd && (
        <div style={{ background: '#fff', border: '1px solid #378ADD', borderRadius: 10, padding: '20px 24px', marginBottom: 20 }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 16 }}>New bundle</div>
          <BundleForm
            onSave={handleCreate}
            onCancel={() => { setShowAdd(false); setAddError(''); }}
            saving={addSaving}
            error={addError}
          />
        </div>
      )}

      {/* ── Active bundles ── */}
      {active.length === 0 && !showAdd && (
        <div className="loading">No active bundles. Add one above.</div>
      )}
      {active.map(b => (
        <BundleCard key={b.id} bundle={b} onUpdated={handleUpdated} />
      ))}

      {/* ── Inactive bundles (collapsible) ── */}
      {inactive.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <button
            onClick={() => setShowInactive(f => !f)}
            style={{ fontSize: 12, color: '#aaa', background: 'none', border: 'none', cursor: 'pointer', padding: '4px 0', marginBottom: 10 }}
          >
            {showInactive ? '▲ Hide' : '▼ Show'} {inactive.length} inactive bundle{inactive.length !== 1 ? 's' : ''}
          </button>
          {showInactive && inactive.map(b => (
            <BundleCard key={b.id} bundle={b} onUpdated={handleUpdated} />
          ))}
        </div>
      )}
    </>
  );
}
